import React, { useState } from 'react';
import { getStreamVideoUrl, getStreamThumbnailUrl } from '../utils/cloudflareStream';

/**
 * CloudflareVideo Component
 * Renders a Cloudflare Stream video with optional thumbnail
 */
const CloudflareVideo = ({
  videoId,
  width = 640,
  height = 360,
  autoplay = false,
  loop = false,
  muted = true,
  controls = true,
  showThumbnail = true,
  thumbnailTime = 0,
  className = '',
  style = {},
  onLoad,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Generate video URL with options
  const videoUrl = getStreamVideoUrl(videoId, {
    width,
    height,
    autoplay,
    loop,
    muted,
  });

  // Generate thumbnail URL
  const thumbnailUrl = showThumbnail 
    ? getStreamThumbnailUrl(videoId, {
        width,
        height,
        fit: 'cover',
        time: thumbnailTime,
      })
    : null;

  const handleLoad = () => {
    setIsLoading(false);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    if (onError) onError();
  };

  if (hasError) {
    return (
      <div 
        className={`cloudflare-video-error ${className}`}
        style={{ 
          width, 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          color: '#666',
          ...style 
        }}
      >
        Failed to load video
      </div>
    );
  }

  return (
    <div className={`cloudflare-video-container ${className}`} style={style}>
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            width,
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f0f0f0',
            zIndex: 1,
          }}
        >
          {thumbnailUrl && (
            <img 
              src={thumbnailUrl} 
              alt="Video thumbnail"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                opacity: 0.7 
              }}
            />
          )}
          <div 
            style={{
              position: 'absolute',
              color: '#666',
              fontSize: '14px',
            }}
          >
            Loading video...
          </div>
        </div>
      )}
      <iframe
        src={videoUrl}
        width={width}
        height={height}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        style={{
          border: 'none',
          borderRadius: '8px',
        }}
        onLoad={handleLoad}
        onError={handleError}
        title={`Cloudflare Stream Video ${videoId}`}
      />
    </div>
  );
};

/**
 * ResponsiveCloudflareVideo Component
 * Responsive version that maintains aspect ratio
 */
export const ResponsiveCloudflareVideo = ({
  videoId,
  aspectRatio = '16:9',
  maxWidth = '100%',
  ...otherProps
}) => {
  const [width, height] = aspectRatio.split(':').map(Number);
  const paddingTop = `${(height / width) * 100}%`;

  return (
    <div 
      style={{ 
        position: 'relative',
        width: '100%',
        maxWidth,
        paddingTop,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <CloudflareVideo
          videoId={videoId}
          width="100%"
          height="100%"
          {...otherProps}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      </div>
    </div>
  );
};

export default CloudflareVideo;